/**
 * effectFootprint — an area effect drawn at its gameplay radius (plan 29 F6,
 * lane BS 2026-10-11).
 *
 * `AOEBehavior` used to spawn its zone effect at `scale: vfxScale ?? 1`, a
 * number unrelated to the radius the server hits: Rain of Arrows drew ~520 px
 * for a 150 px hit, Blizzard ~400/200, Meteor ~300/150, Explosive Shot
 * ~135/80, Prayer of Mending ~106/150. Worse, a play-time `scale` only grows
 * each SHAPE — offsets, scatter rings, orbits and tween travel stay at their
 * authored pixels — so no single scale number could make a def match a radius.
 *
 * The contract now matches the totem aura ring (SummonVFXHandler: a ring drawn
 * at exactly `radius`):
 *
 *  - `effectFootprintPx(def)` — the def's horizontal reach from its origin at
 *    authored size: the farthest any shape gets (offset + scatter ring + its
 *    own half-size at its largest tweened scale + tween travel, or an orbit).
 *    Horizontal, because a ground zone reads by its width; things that fall
 *    from above (snow, arrows) may start higher than the zone is wide.
 *  - `fitEffectToArea(def, radiusPx)` — the same def with every length
 *    scaled uniformly (shape sizes AND offsets, scatter rings, orbits, tween
 *    travel), so its footprint equals the radius.
 *
 * `VFXEffectPlayer.play` applies it when a spawn carries `areaRadius`; every
 * area behaviour sends its gameplay radius there instead of a free scale.
 * Particle-system primitives (emitter presets) have no authored extent and
 * are left as they are.
 */
import type { VFXEffectConfig, VFXPrimitive } from '../VFXEffectRegistry';

function maxTweenScale(prim: VFXPrimitive): number {
  const t = prim.tween;
  if (!t) return 1;
  return Math.max(1, t.scale ?? 1, t.scaleX ?? 1);
}

/** Half-width of one shape at authored size (px), before tweening. */
function shapeHalfWidth(prim: VFXPrimitive): number {
  switch (prim.type) {
    case 'circle': return prim.radius ?? 0;
    case 'ellipse': return (prim.width ?? (prim.radius ?? 0) * 2) / 2;
    case 'rect': return (prim.width ?? 0) / 2;
    case 'star': return prim.outerRadius ?? prim.radius ?? 0;
    case 'polygon': {
      const pts = prim.polygonPoints ?? [];
      let m = 0;
      for (let i = 0; i < pts.length; i += 2) m = Math.max(m, Math.abs(pts[i]));
      return m;
    }
    case 'arc': return prim.arcRadius ?? prim.radius ?? 0;
    case 'cone': return prim.coneRadius ?? prim.radius ?? 0;
    case 'shockwave': return prim.shockwaveOuterRadius ?? prim.radius ?? 0;
    case 'beam': return prim.beamLength ?? 0;
    case 'line': return Math.max(0, ...(prim.linePoints ?? []).map((p) => Math.abs(p.x)));
    default: return 0;
  }
}

/** One primitive's horizontal reach from the effect origin (px). */
export function primitiveFootprintPx(prim: VFXPrimitive): number {
  if (prim.type === 'particles' || prim.type === 'entity_override' || prim.type === 'ribbon') return 0;
  const size = shapeHalfWidth(prim) * maxTweenScale(prim);
  if (prim.orbit) return Math.abs(prim.orbit.centerOffsetX ?? 0) + prim.orbit.radius + size;
  const travel = Math.abs(prim.tween?.x ?? 0);
  return Math.abs(prim.offsetX ?? 0) + (prim.scatter?.radius ?? 0) + size + travel;
}

/** The def's horizontal reach from its origin at authored size (px). */
export function effectFootprintPx(def: Pick<VFXEffectConfig, 'primitives'>): number {
  let m = 0;
  for (const prim of def.primitives) m = Math.max(m, primitiveFootprintPx(prim));
  return m;
}

const LENGTH_KEYS = [
  'offsetX', 'offsetY', 'radius', 'width', 'height', 'innerRadius', 'outerRadius',
  'arcRadius', 'coneRadius', 'beamLength', 'beamWidth', 'shockwaveInnerRadius', 'shockwaveOuterRadius',
] as const;

/** `prim` with every length multiplied by `s` (angles, colours, timing untouched). */
export function scalePrimitive(prim: VFXPrimitive, s: number): VFXPrimitive {
  const out: VFXPrimitive = { ...prim };
  for (const k of LENGTH_KEYS) {
    const v = prim[k];
    if (typeof v === 'number') (out as unknown as Record<string, unknown>)[k] = v * s;
  }
  if (prim.polygonPoints) out.polygonPoints = prim.polygonPoints.map((v) => v * s);
  if (prim.linePoints) out.linePoints = prim.linePoints.map((p) => ({ x: p.x * s, y: p.y * s }));
  if (prim.scatter) out.scatter = { ...prim.scatter, radius: prim.scatter.radius * s };
  if (prim.orbit) {
    out.orbit = {
      ...prim.orbit,
      radius: prim.orbit.radius * s,
      ...(prim.orbit.centerOffsetX !== undefined ? { centerOffsetX: prim.orbit.centerOffsetX * s } : {}),
      ...(prim.orbit.centerOffsetY !== undefined ? { centerOffsetY: prim.orbit.centerOffsetY * s } : {}),
    };
  }
  if (prim.tween && (prim.tween.x !== undefined || prim.tween.y !== undefined)) {
    out.tween = {
      ...prim.tween,
      ...(prim.tween.x !== undefined ? { x: prim.tween.x * s } : {}),
      ...(prim.tween.y !== undefined ? { y: prim.tween.y * s } : {}),
    };
  }
  return out;
}

/** The uniform scale that makes `def`'s footprint equal `radiusPx` (1 when it has none). */
export function areaScaleFor(def: Pick<VFXEffectConfig, 'primitives'>, radiusPx: number): number {
  const footprint = effectFootprintPx(def);
  if (!(footprint > 0) || !(radiusPx > 0)) return 1;
  return radiusPx / footprint;
}

/** `def` scaled uniformly so its footprint equals `radiusPx`. */
export function fitEffectToArea<T extends Pick<VFXEffectConfig, 'primitives'>>(def: T, radiusPx: number): T {
  const s = areaScaleFor(def, radiusPx);
  if (s === 1) return def;
  return { ...def, primitives: def.primitives.map((p) => scalePrimitive(p, s)) };
}

/**
 * `VFXEffectPlayer.play`'s hook: a spawn carrying `areaRadius` (its gameplay
 * radius) plays the def fitted to it, and any free `scale` is dropped — a
 * scale only grows each shape, never the layout.
 */
export function applyAreaRadius<C extends Pick<VFXEffectConfig, 'primitives'>, O extends { [key: string]: unknown } | undefined>(
  config: C, overrides: O,
): [C, O] {
  const areaRadius = overrides?.areaRadius;
  if (typeof areaRadius !== 'number' || !(areaRadius > 0)) return [config, overrides];
  return [fitEffectToArea(config, areaRadius), { ...overrides, scale: undefined } as O];
}
